import {
  ReactElement,
  FC,
  ReactNode,
  ElementType,
  SyntheticEvent,
  forwardRef,
  useState,
} from 'react'
import { Tooltip, TooltipProps, SvgIconProps, ToggleButtonProps } from '@mui/material'
import { ToggleButton as StyledToggleButton } from './styles'

interface Props extends Omit<ToggleButtonProps, 'value'> {
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  selected?: boolean
  hint?: string
  /** Use `top` for controls docked to the bottom of the viewport. */
  tooltipPlacement?: TooltipProps['placement']
  Icon: FC<SvgIconProps>
  disabled?: boolean
  children?: ReactNode
  value?: string
  inverted?: boolean
  component?: ElementType<HTMLDivElement>
  iconSize?: 'small' | 'inherit' | 'medium' | 'large'
  iconColour?: 'primary' | 'secondary' | 'inherit'
}

type TooltipState = 'closed' | 'open' | 'dismissed'

const ToggleButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      hint = '',
      tooltipPlacement = 'bottom',
      Icon,
      iconSize = 'small',
      selected,
      disabled,
      onClick,
      onMouseLeave,
      children,
      value = 'snap',
      inverted = false,
      iconColour = 'inherit',
      ...buttonProps
    },
    ref,
  ): ReactElement => {
    const [tooltipState, setTooltipState] = useState<TooltipState>('closed')

    const resetTooltip = () => setTooltipState('closed')

    const handleTooltipOpen = (event: SyntheticEvent) => {
      const openedByHover = event.type === 'mouseover'
      setTooltipState(state => (state === 'dismissed' && openedByHover ? state : 'open'))
    }

    const button = (
      <StyledToggleButton
        ref={ref}
        size="small"
        aria-label={hint || undefined}
        onClick={event => {
          setTooltipState('dismissed')
          onClick?.(event)
        }}
        onMouseLeave={event => {
          resetTooltip()
          onMouseLeave?.(event)
        }}
        selected={selected}
        disabled={disabled}
        className="MuiToggleButtonGroup-grouped MuiToggleButtonGroup-groupedHorizontal"
        value={value}
        inverted={inverted}
        {...{
          ...buttonProps,
        }}
      >
        <Icon fontSize={iconSize} color={iconColour} />
        {children}
      </StyledToggleButton>
    )

    return (
      <Tooltip
        title={hint}
        placement={tooltipPlacement}
        open={tooltipState === 'open'}
        onOpen={handleTooltipOpen}
        onClose={resetTooltip}
        disableInteractive
      >
        {disabled ? <span onMouseLeave={resetTooltip}>{button}</span> : button}
      </Tooltip>
    )
  },
)

ToggleButton.displayName = 'ToggleButton'

export default ToggleButton
